<template>
  <section class="archive-detail">
    <div class="detail-heading">
      <button type="button" class="back-button" @click="$emit('back')">
        <span class="material-icons-outlined" aria-hidden="true">arrow_back</span>
        Archives
      </button>
      <div>
        <h3>{{ archive.authEmail || archive.notificationEmail || "Archived App Client" }}</h3>
        <p>{{ archive.uid || archive.id }}</p>
      </div>
      <span class="priority-pill" :class="archive.reviewPriority || 'standard'">
        {{ archive.reviewPriority === "urgent" ? "Urgent Review" : "Standard Review" }}
      </span>
    </div>

    <div class="detail-grid">
      <section class="detail-card review-card">
        <div class="card-heading">
          <div>
            <span class="eyebrow">Staff workflow</span>
            <h4>Archive Review & Follow-up</h4>
          </div>
          <span class="review-indicator" :class="reviewStatus">
            {{ reviewStatusLabel }}
          </span>
        </div>

        <div v-if="archive.hasOpenMatters" class="open-matter-alert">
          <span class="material-icons-outlined" aria-hidden="true">warning</span>
          This App Client left with {{ archive.openMatterCount || 0 }} open matter(s). Review and follow-up are tracked separately.
        </div>

        <div class="form-grid">
          <label>
            Review status
            <select v-model="reviewStatus">
              <option value="pending">Pending</option>
              <option value="inReview">In Review</option>
              <option value="reviewed">Reviewed</option>
            </select>
          </label>
          <label>
            Action status
            <select v-model="actionStatus">
              <option value="notRequired">Not Required</option>
              <option value="required">Required</option>
              <option value="inProgress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </label>
        </div>

        <label class="summary-label">
          Review notes / action taken
          <textarea
            v-model="actionSummary"
            maxlength="4000"
            rows="4"
            placeholder="Record what was reviewed, who was contacted, and any action taken."
          />
        </label>

        <div class="review-meta">
          <span>Last workflow update: {{ formatDateTime(archive.reviewUpdatedAt) }}</span>
          <span v-if="archive.reviewUpdatedByName">by {{ archive.reviewUpdatedByName }}</span>
        </div>
        <div class="review-history">
          <span>Reviewed: {{ formatDateTime(archive.reviewedAt) }}<template v-if="archive.reviewedByName"> by {{ archive.reviewedByName }}</template></span>
          <span>Action completed: {{ formatDateTime(archive.actionTakenAt) }}<template v-if="archive.actionTakenByName"> by {{ archive.actionTakenByName }}</template></span>
        </div>

        <div v-if="saveError" class="inline-error">{{ saveError }}</div>
        <div v-if="saveMessage" class="inline-success">{{ saveMessage }}</div>

        <button type="button" class="save-button" :disabled="saving || !hasChanges" @click="saveReview">
          {{ saving ? "Saving..." : "Save Archive Review" }}
        </button>
      </section>

      <section class="detail-card">
        <span class="eyebrow">Identity</span>
        <h4>Account Record</h4>
        <dl>
          <div><dt>Firebase email</dt><dd>{{ valueOrDash(archive.authEmail) }}</dd></div>
          <div><dt>Notification email</dt><dd>{{ valueOrDash(archive.notificationEmail) }}</dd></div>
          <div><dt>Firebase UID</dt><dd class="mono">{{ valueOrDash(archive.uid || archive.id) }}</dd></div>
          <div><dt>Provider(s)</dt><dd>{{ listValue(archive.authProviderIds) }}</dd></div>
          <div><dt>Archive reason</dt><dd>{{ labelValue(archive.archiveReason) }}</dd></div>
          <div><dt>Archive status</dt><dd>{{ labelValue(archive.status) }}</dd></div>
        </dl>
      </section>

      <section class="detail-card">
        <span class="eyebrow">Dates</span>
        <h4>Account Lifecycle</h4>
        <dl>
          <div><dt>Requested</dt><dd>{{ formatDateTime(archive.requestedAt) }}</dd></div>
          <div><dt>Archived</dt><dd>{{ formatDateTime(archive.archivedAt) }}</dd></div>
          <div><dt>Auth deleted</dt><dd>{{ formatDateTime(archive.authDeletedAt) }}</dd></div>
          <div><dt>Last sign-in</dt><dd>{{ formatDateTime(archive.lastAuthSignInAt) }}</dd></div>
          <div><dt>Last activity</dt><dd>{{ formatDateTime(archive.lastActivityAt) }}</dd></div>
          <div><dt>Relationship ended</dt><dd>{{ formatDateTime(archive.relationshipEndedAt) }}</dd></div>
        </dl>
      </section>

      <section class="detail-card matters-card">
        <span class="eyebrow">Legal work</span>
        <h4>Litigation & Applications</h4>
        <dl>
          <div><dt>Open matters</dt><dd>{{ archive.openMatterCount || 0 }}</dd></div>
          <div><dt>Incomplete applications</dt><dd>{{ archive.incompleteApplicationCount || 0 }}</dd></div>
          <div><dt>Open matter flag</dt><dd>{{ yesNo(archive.hasOpenMatters) }}</dd></div>
        </dl>
        <div class="id-list">
          <strong>Open Litigation IDs</strong>
          <span v-if="!archive.openMatterIds?.length">None</span>
          <code v-for="matterId in archive.openMatterIds" :key="matterId">{{ matterId }}</code>
        </div>
      </section>

      <section class="detail-card">
        <span class="eyebrow">Retention</span>
        <h4>Legal Hold & Destruction</h4>
        <dl>
          <div><dt>Legal hold</dt><dd>{{ yesNo(archive.legalHold) }}</dd></div>
          <div><dt>Hold reason</dt><dd>{{ valueOrDash(archive.legalHoldReason) }}</dd></div>
          <div><dt>Hold set</dt><dd>{{ formatDateTime(archive.legalHoldSetAt) }}</dd></div>
          <div><dt>Hold released</dt><dd>{{ formatDateTime(archive.legalHoldReleasedAt) }}</dd></div>
          <div><dt>Policy version</dt><dd>{{ valueOrDash(archive.retentionPolicyVersion) }}</dd></div>
          <div><dt>Retention basis</dt><dd>{{ listValue(archive.retentionBasis) }}</dd></div>
          <div><dt>Retention started</dt><dd>{{ formatDateTime(archive.retentionStartedAt) }}</dd></div>
          <div><dt>Retention review</dt><dd>{{ formatDateTime(archive.retentionReviewAt) }}</dd></div>
          <div><dt>Eligible for destruction</dt><dd>{{ formatDateTime(archive.eligibleForDestructionAt) }}</dd></div>
          <div><dt>Destruction approved</dt><dd>{{ formatDateTime(archive.destructionApprovedAt) }}</dd></div>
          <div><dt>Approved by</dt><dd>{{ valueOrDash(archive.destructionApprovedBy) }}</dd></div>
          <div><dt>Fully deleted</dt><dd>{{ formatDateTime(archive.fullyDeletedAt) }}</dd></div>
          <div><dt>Deletion verified</dt><dd>{{ formatDateTime(archive.deletionVerifiedAt) }}</dd></div>
        </dl>
      </section>

      <section class="detail-card">
        <span class="eyebrow">Data access</span>
        <h4>Requests & Restoration</h4>
        <dl>
          <div><dt>Data requests</dt><dd>{{ archive.dataRequestCount || 0 }}</dd></div>
          <div><dt>Last requested</dt><dd>{{ formatDateTime(archive.lastDataRequestedAt) }}</dd></div>
          <div><dt>Last delivered</dt><dd>{{ formatDateTime(archive.lastDataDeliveredAt) }}</dd></div>
          <div><dt>Restoration eligible</dt><dd>{{ yesNo(archive.restorationEligible) }}</dd></div>
          <div><dt>Restored</dt><dd>{{ formatDateTime(archive.restoredAt) }}</dd></div>
          <div><dt>Restored by</dt><dd>{{ valueOrDash(archive.restoredBy) }}</dd></div>
          <div><dt>Replacement UID</dt><dd class="mono">{{ valueOrDash(archive.replacementUid) }}</dd></div>
        </dl>
      </section>

      <section class="detail-card locations-card">
        <span class="eyebrow">Record map</span>
        <h4>Retained Data Locations</h4>
        <dl>
          <div v-for="(path, key) in archive.dataLocations" :key="key">
            <dt>{{ labelValue(key) }}</dt>
            <dd class="mono">{{ path }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </section>
</template>

<script>
import { computed, ref, watch } from "vue";

import { updateArchivedClientReview } from "@/firebase/firestore";

export default {
  name: "ArchiveDetailViewer",

  emits: ["back", "updated"],

  props: {
    archive: {
      type: Object,
      required: true
    }
  },

  setup(props, { emit }) {
    const reviewStatus = ref(props.archive.reviewStatus ?? "pending");
    const actionStatus = ref(props.archive.actionStatus ?? (props.archive.hasOpenMatters ? "required" : "notRequired"));
    const actionSummary = ref(props.archive.actionSummary ?? "");
    const saving = ref(false);
    const saveError = ref("");
    const saveMessage = ref("");

    watch(() => props.archive, archive => {
      reviewStatus.value = archive.reviewStatus ?? "pending";
      actionStatus.value = archive.actionStatus ?? (archive.hasOpenMatters ? "required" : "notRequired");
      actionSummary.value = archive.actionSummary ?? "";
    });

    const hasChanges = computed(() => {
      return reviewStatus.value !== (props.archive.reviewStatus ?? "pending") ||
        actionStatus.value !== (props.archive.actionStatus ?? (props.archive.hasOpenMatters ? "required" : "notRequired")) ||
        actionSummary.value.trim() !== String(props.archive.actionSummary ?? "").trim();
    });
    const reviewStatusLabel = computed(() => ({
      pending: "Pending", inReview: "In Review", reviewed: "Reviewed"
    }[reviewStatus.value] ?? "Pending"));

    const saveReview = async () => {
      saving.value = true;
      saveError.value = "";
      saveMessage.value = "";

      try {
        const result = await updateArchivedClientReview({
          uid: props.archive.uid || props.archive.id,
          reviewStatus: reviewStatus.value,
          actionStatus: actionStatus.value,
          actionSummary: actionSummary.value.trim()
        });
        const updatedArchive = {
          ...props.archive,
          ...result,
          id: props.archive.id,
          reviewedAt: result.reviewedAt ? new Date(result.reviewedAt) : props.archive.reviewedAt,
          actionTakenAt: result.actionTakenAt ? new Date(result.actionTakenAt) : props.archive.actionTakenAt,
          reviewUpdatedAt: result.reviewUpdatedAt ? new Date(result.reviewUpdatedAt) : props.archive.reviewUpdatedAt
        };
        emit("updated", updatedArchive);
        saveMessage.value = "Archive workflow saved.";
      } catch (error) {
        console.error("❌ Unable to save archive review:", error);
        saveError.value = error?.message ?? "Unable to save the archive review.";
      } finally {
        saving.value = false;
      }
    };

    const dateValue = value => value?.toDate ? value.toDate() : new Date(value ?? 0);
    const formatDateTime = value => {
      if (!value) { return "—"; }
      const date = dateValue(value);
      return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString(undefined, {
        year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
      });
    };
    const valueOrDash = value => String(value ?? "").trim() || "—";
    const listValue = value => Array.isArray(value) && value.length ? value.join(", ") : "—";
    const yesNo = value => value === true ? "Yes" : "No";
    const labelValue = value => String(value ?? "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .replace(/^./, character => character.toUpperCase()) || "—";

    return {
      reviewStatus,
      actionStatus,
      actionSummary,
      saving,
      saveError,
      saveMessage,
      hasChanges,
      reviewStatusLabel,
      saveReview,
      formatDateTime,
      valueOrDash,
      listValue,
      yesNo,
      labelValue
    };
  }
};
</script>

<style scoped>
.archive-detail { display: grid; gap: 18px; }
.detail-heading { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 16px; }
.detail-heading h3 { margin: 0; font-size: 1.35rem; overflow-wrap: anywhere; }
.detail-heading p { margin: 4px 0 0; color: #77827d; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.78rem; }
.back-button { display: flex; align-items: center; gap: 7px; padding: 9px 13px; border: 1px solid #d9e0dc; border-radius: 9px; background: #fff; color: #34423b; font: inherit; cursor: pointer; }
.priority-pill, .review-indicator { padding: 6px 10px; border-radius: 999px; background: #edf2ef; color: #52615a; font-size: 0.8rem; font-weight: 700; }
.priority-pill.urgent { background: #fff0e5; color: #a55218; }
.detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 15px; }
.detail-card { min-width: 0; padding: 20px; border: 1px solid #dfe5e1; border-radius: 14px; background: #fff; }
.detail-card.review-card, .detail-card.locations-card { grid-column: 1 / -1; }
.eyebrow { color: #75817b; font-size: 0.72rem; font-weight: 750; letter-spacing: 0.08em; text-transform: uppercase; }
.detail-card h4 { margin: 5px 0 17px; font-size: 1.06rem; }
.card-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; }
.review-indicator.pending { background: #fff4dd; color: #966817; }
.review-indicator.inReview { background: #e8f2ff; color: #27669b; }
.review-indicator.reviewed { background: #e5f6ed; color: #18764b; }
.open-matter-alert { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 16px; padding: 12px 14px; border-radius: 10px; background: #fff3e9; color: #8d4a19; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
label { display: grid; gap: 7px; color: #53605a; font-size: 0.84rem; font-weight: 650; }
select, textarea { width: 100%; box-sizing: border-box; padding: 10px 12px; border: 1px solid #d6ded9; border-radius: 9px; background: #fff; color: #27332e; font: inherit; }
textarea { resize: vertical; line-height: 1.45; }
.summary-label { margin-top: 14px; }
.review-meta { display: flex; gap: 5px; margin-top: 10px; color: #7a8580; font-size: 0.78rem; }
.review-history { display: grid; gap: 4px; margin-top: 6px; color: #7a8580; font-size: 0.78rem; }
.save-button { margin-top: 15px; padding: 11px 17px; border: 0; border-radius: 9px; background: #159c62; color: #fff; font: inherit; font-weight: 700; cursor: pointer; }
.save-button:disabled { opacity: 0.55; cursor: default; }
.inline-error, .inline-success { margin-top: 12px; padding: 10px 12px; border-radius: 8px; font-size: 0.86rem; }
.inline-error { background: #fff0f0; color: #9c3838; }
.inline-success { background: #eaf7f0; color: #18764b; }
dl { display: grid; gap: 0; margin: 0; }
dl > div { display: grid; grid-template-columns: minmax(125px, 0.8fr) minmax(0, 1.2fr); gap: 14px; padding: 10px 0; border-top: 1px solid #edf0ee; }
dl > div:first-child { border-top: 0; }
dt { color: #75817b; font-size: 0.82rem; }
dd { margin: 0; overflow-wrap: anywhere; text-align: right; font-weight: 600; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.78rem; }
.id-list { display: grid; gap: 7px; margin-top: 13px; padding-top: 13px; border-top: 1px solid #edf0ee; }
.id-list span { color: #75817b; }
.id-list code { padding: 7px 9px; border-radius: 7px; background: #f3f6f4; overflow-wrap: anywhere; }
@media (max-width: 850px) {
  .detail-grid { grid-template-columns: 1fr; }
  .detail-card.review-card, .detail-card.locations-card { grid-column: auto; }
  .detail-heading { grid-template-columns: auto minmax(0, 1fr); }
  .priority-pill { grid-column: 2; width: fit-content; }
}
@media (max-width: 560px) {
  .form-grid { grid-template-columns: 1fr; }
  dl > div { grid-template-columns: 1fr; gap: 4px; }
  dd { text-align: left; }
}
</style>
